import { playSound, SoundOption } from "@/helpers/playSound";
import { stringContainsOnlyDigits } from "@/helpers/strings";
import { logFirebaseEvent } from "@/services/firebase";
import { useIsTakeOut } from "@/stores/cash-register";
import useModalsStore from "@/stores/modals";
import { useAppDispatch } from "./useAppDispatch";
import { addProduct, addQrCode } from "@/store/cart/thunk";
import { useCallback, useEffect, useRef } from "react";
import ScannerHandler from "@/helpers/ScannerHandler";
import { useAppSelector } from "./useAppSelector";
import { selectInitialEan } from "@/store/ui/selectors";
import { resetInitialEan } from "@/store/ui/slice";
import { selectProducts } from "@/store/product/selectors";

const useScanning = () => {
  const dispatch = useAppDispatch();
  const initialEan = useAppSelector(selectInitialEan);
  const { products, isLoading: isLoadingProducts } = useAppSelector(selectProducts);
  const { isSearchModalOpen, toggleIsSearchModalOpen, setInvalidEanCode } = useModalsStore();
  const isTakeOut = useIsTakeOut();
  const hasAddedStartPageProduct = useRef<boolean>(false);

  const onScan = useCallback(
    async (code: string) => {
      if (!stringContainsOnlyDigits(code)) {
        dispatch(addQrCode(code));
        logFirebaseEvent("qr_scanned");
        return;
      }
      setInvalidEanCode(null);
      if (!products) return;
      const product = products?.find((p) => String(p.eanCode) === code);
      if (isSearchModalOpen) toggleIsSearchModalOpen();
      if (!product) {
        setInvalidEanCode(code);
        playSound(SoundOption.ErrorEan);
        return;
      }
      logFirebaseEvent("product_scanned", {
        id: product.id,
        name: product.translations[0].name,
      });
      return dispatch(addProduct(product.id));
    },
    [dispatch, isSearchModalOpen, products, setInvalidEanCode, toggleIsSearchModalOpen]
  );

  useEffect(() => {
    const scannerHandler = new ScannerHandler(onScan);
    scannerHandler.start();
    return () => scannerHandler.stop();
  }, [onScan]);

  useEffect(() => {
    if (!isTakeOut || !initialEan) return;

    if (!hasAddedStartPageProduct.current && !isLoadingProducts) {
      onScan(initialEan);
      dispatch(resetInitialEan());
      hasAddedStartPageProduct.current = true;
    }
  }, [dispatch, initialEan, isTakeOut, isLoadingProducts, onScan]);
};

export default useScanning;
